/**
 * WEB-853 — режим показа пейволла в контейнере приложения.
 *
 * Три режима:
 *   mandatory — закрыть нельзя, кнопки закрытия нет НИКОГДА;
 *   closable  — кнопка закрытия видна сразу;
 *   delayed   — кнопка закрытия появляется через closeDelaySeconds.
 *
 * ДЕФОЛТ намеренно = 'mandatory'. Сегодня у веб-пейволла нет никакой кнопки
 * закрытия до оплаты — единственная закрывающая механика появляется ПОСЛЕ
 * успешной оплаты. Значит «поля нет» обязано означать ровно сегодняшнее
 * поведение, иначе все уже опубликованные пейволлы молча стали бы
 * закрываемыми.
 *
 * Держать в синхроне с backend/src/ai-funnels/block-schema/funnel-settings.ts
 * (PAYWALL_CLOSE_MODES + closeDelaySeconds 1..600).
 */

import { pickStrings } from '@/i18n';
import { editorStrings } from '@/i18n/dictionaries/editor';

export const PAYWALL_CLOSE_MODES = ['mandatory', 'closable', 'delayed'] as const;

export type PaywallCloseMode = (typeof PAYWALL_CLOSE_MODES)[number];

/** Задержка по умолчанию, когда режим delayed выбран, а секунды не заданы. */
export const DEFAULT_CLOSE_DELAY_SECONDS = 5;

/** Границы задержки — те же, что в zod-схеме бэкенда. */
export const MIN_CLOSE_DELAY_SECONDS = 1;
export const MAX_CLOSE_DELAY_SECONDS = 600;

export interface ResolvedPaywallCloseMode {
  mode: PaywallCloseMode;
  /** Секунды до показа кнопки. Осмысленны только при mode === 'delayed'. */
  delaySeconds: number;
}

/**
 * Подписи для кабинета — одно место, чтобы редактор и подсказки не разъезжались.
 * Функция, а не константа: язык кабинета известен только в момент отрисовки
 * (WEB-1874), готовый объект застыл бы на языке импорта модуля.
 */
export function paywallCloseModeLabels(): Record<PaywallCloseMode, string> {
  const s = pickStrings(editorStrings);
  return {
    mandatory: s.closeModeMandatory,
    closable: s.closeModeClosable,
    delayed: s.closeModeDelayed,
  };
}

/** Короткие подписи — для кнопки в тулбаре редактора (там мало места). */
export function paywallCloseModeShortLabels(): Record<PaywallCloseMode, string> {
  const s = pickStrings(editorStrings);
  return {
    mandatory: s.closeModeShortMandatory,
    closable: s.closeModeShortClosable,
    delayed: s.closeModeShortDelayed,
  };
}

function isCloseMode(value: unknown): value is PaywallCloseMode {
  return (
    typeof value === 'string' &&
    (PAYWALL_CLOSE_MODES as readonly string[]).includes(value)
  );
}

/**
 * Разбирает settings пейволла в предсказуемый режим показа.
 *
 * Всё негодное (неизвестный режим, дробная/отрицательная/огромная задержка,
 * задержка строкой) схлопывается в безопасное значение, а не в «показать
 * кнопку прямо сейчас»: испорченная настройка не должна отдавать пейволл
 * бесплатно.
 */
export function resolvePaywallCloseMode(
  settings: Record<string, unknown> | null | undefined,
): ResolvedPaywallCloseMode {
  const rawMode = settings?.['closeMode'];
  const mode: PaywallCloseMode = isCloseMode(rawMode) ? rawMode : 'mandatory';

  const rawDelay = settings?.['closeDelaySeconds'];
  const delaySeconds =
    typeof rawDelay === 'number' &&
    Number.isInteger(rawDelay) &&
    rawDelay >= MIN_CLOSE_DELAY_SECONDS &&
    rawDelay <= MAX_CLOSE_DELAY_SECONDS
      ? rawDelay
      : DEFAULT_CLOSE_DELAY_SECONDS;

  return { mode, delaySeconds };
}
